import { motion } from "framer-motion";
import { Cpu, Network, Code2, Megaphone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { headingFade, springEntrance, stagger, useCardTilt } from "@/lib/motion";

const services = [
  {
    title: "AI Automation",
    icon: Cpu,
    description: "We design and deploy agentic workflows that operate autonomously alongside your team. Stop manually bridging the gaps between your software—let custom AI agents handle the repetitive data entry, triage, and processing work with total accuracy.",
  },
  {
    title: "Process Enablement",
    icon: Network,
    description: "Technology fails when it doesn't fit the human workflow. We re-architect how your team operates so that AI naturally augments your people instead of feeling like a bolted-on distraction, unlocking true operational scale.",
  },
  {
    title: "Development Support",
    icon: Code2,
    description: "Take your software concepts from prototype to production with engineering rigor. We provide AI-accelerated development support to rapidly build and ship internal tools, custom integrations, and specialized products.",
  },
  {
    title: "Marketing Automation",
    icon: Megaphone,
    description: "Deploy agentic marketing pipelines that personalize, publish, and measure at an SMB scale. We build intelligent systems that analyze engagement, dynamically generate content, and optimize your outreach continuously.",
  },
];

function ServiceCard({ service }: { service: typeof services[number] }) {
  const tiltRef = useCardTilt(3);
  return (
    <motion.div variants={springEntrance} className="will-change-transform">
      <div ref={tiltRef} className="h-full transition-transform duration-150 ease-out [transform-style:preserve-3d]">
        <Card className="bg-card border-border/50 hover:border-primary/50 hover:ring-2 hover:ring-orange-400/40 transition-all duration-200 h-full overflow-hidden group relative">
          {/* Cursor-tracking shine */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background:
                "radial-gradient(circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.08), transparent 40%)",
            }}
          />
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-primary/20 group-hover:-rotate-3 transition-all duration-300">
              <service.icon className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-display text-foreground">{service.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground leading-relaxed">{service.description}</p>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
}

export function Services() {
  return (
    <section id="services" className="py-24 md:py-32 relative">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          className="max-w-3xl mb-16"
          variants={headingFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <h2 className="text-primary font-mono text-sm tracking-widest uppercase mb-3">Service Pillars</h2>
          <h3 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-6">
            Engineering the future of your operations.
          </h3>
          <p className="text-lg text-muted-foreground">
            We don't just sell software licenses. We build tailored, intelligent systems designed to give your mid-sized business the capabilities of an enterprise.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={stagger(0.09)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
